import { createContext, useContext, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';

import type { Handler } from './types';
import { Avatar, AvatarFallback } from '~/components/ui/avatar';
import { cn } from '~/lib/utils';

/** Controls drawn on the bubble's own surface. They inherit its text colour
 *  instead of naming one, so a single class works on grey and on blue. */
export const ON_SURFACE_CONTROL =
  'text-current opacity-70 hover:bg-current/10 hover:text-current hover:opacity-100';

/** The same, for text links in the footer, which carry no chrome of their own. */
export const ON_SURFACE_LINK =
  'h-auto px-0 py-0 text-xs font-medium text-current opacity-90 hover:text-current hover:opacity-100';

/**
 * `outlined` is the same grey surface as an ordinary inbound bubble, drawn
 * rather than only filled and sitting a little heavier. Deliberately not a
 * colour: green means good news everywhere else here, and an enquiry is not
 * news at all.
 */
type BubbleTone = 'default' | 'outlined';

const OutboundContext = createContext(false);

export function useIsOutbound() {
  return useContext(OutboundContext);
}

export function MessageRow({
  isOutbound,
  handledBy,
  children,
}: {
  isOutbound: boolean;
  handledBy: Array<Handler>;
  children: React.ReactNode;
}) {
  return (
    <article
      className={cn('flex', isOutbound ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'flex w-full max-w-bubble items-end gap-1.5',
          isOutbound && 'flex-row-reverse',
        )}
      >
        <MessageHandlers handledBy={handledBy} isOutbound={isOutbound} />
        {children}
      </div>
    </article>
  );
}

/**
 * Who on our side handled this, and nobody else.
 *
 * A customer never appears here — the row's side already says whose words these
 * are. So an inbound message shows an empty slot, and a call answered by two
 * receptionists shows both, stacked in the order they took it. Stacking rather
 * than spreading is what keeps the column one avatar wide however many there
 * are, so every bubble in the feed starts at the same edge.
 */
export function MessageHandlers({
  handledBy,
  isOutbound,
}: {
  handledBy: Array<Handler>;
  isOutbound: boolean;
}) {
  /** Nobody on our side, but the column still holds its place — a feed whose
   *  bubbles start at different x depending on who answered is worse than an
   *  empty slot. */
  if (handledBy.length === 0) {
    return <MessageAvatarSpacer />;
  }

  return (
    <div className="flex shrink-0 flex-col gap-0.5">
      {handledBy.map((handler) => (
        <MessageAvatar
          key={handler.userId}
          initials={handler.name ? getInitials(handler.name) : '?'}
          isOutbound={isOutbound}
          title={handler.name ?? undefined}
        />
      ))}
    </div>
  );
}

/**
 * The bubble every channel shares: one surface, one radius, one squared corner
 * on the side the avatar sits. `actions` is pinned to the top-right rather than
 * placed in the flow, so a message with no subject still has somewhere to put
 * its menu.
 */
export function MessageBubble({
  isOutbound,
  tone = 'default',
  isHighlighted = false,
  actions,
  className,
  children,
}: {
  isOutbound: boolean;
  tone?: BubbleTone;
  isHighlighted?: boolean;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <OutboundContext.Provider value={isOutbound}>
      <div
        className={cn(
          'relative flex min-w-0 flex-1 flex-col gap-2 rounded-2xl p-3',
          tone === 'outlined'
            ? 'rounded-bl-sm border-2 border-muted-foreground bg-muted p-4 text-foreground'
            : isOutbound
              ? 'rounded-br-sm bg-primary text-primary-foreground'
              : 'rounded-bl-sm bg-muted text-foreground',
          isHighlighted && 'ring-2 ring-primary',
          className,
        )}
      >
        {children}
        {actions && <div className="absolute right-2 top-2">{actions}</div>}
      </div>
    </OutboundContext.Provider>
  );
}

/** `Subject: <subject>`, ruled off from the body below it. The right padding
 *  keeps a long subject clear of the pinned actions menu. */
export function MessageBubbleSubject({ subject }: { subject: string }) {
  const { t } = useTranslation('tasks');
  const isOutbound = useIsOutbound();

  return (
    <p
      className={cn(
        'min-w-0 truncate border-b pb-2.5 pr-6 text-sm',
        isOutbound ? 'border-white/10' : 'border-border',
      )}
    >
      <span className="opacity-80">{t('Subject')}: </span>
      <span className="font-medium">{subject}</span>
    </p>
  );
}

/**
 * The channel and the time, right-aligned, with room on the left for whatever
 * the message wants to offer — the design puts `Show more` there.
 */
export function MessageBubbleFooter({
  icon: Icon,
  label,
  leading,
  status,
}: {
  icon: LucideIcon;
  label: string;
  leading?: React.ReactNode;
  /** How the message went, beside when it went — in the footer's own colour,
   *  which is the bubble's. */
  status?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      {leading ?? <span />}
      <span className="flex shrink-0 items-center gap-2 text-2xs opacity-80">
        {status}
        <span className="flex items-center gap-1">
          <Icon className="size-3" strokeWidth={2.5} />
          {label}
        </span>
      </span>
    </div>
  );
}

/**
 * A long message cut to the design's height, with the overflow faded out.
 *
 * The measurement only runs while collapsed. Expanding makes the content fit by
 * definition, so re-measuring then would conclude it never overflowed and take
 * the control away mid-read.
 */
export function ClampedContent({
  isExpanded,
  isOverflowing,
  onOverflowChange,
  className,
  children,
}: {
  isExpanded: boolean;
  /** Measured here, held by the caller — it decides whether to offer the
   *  control, and this only draws the fade. Two copies of one fact drift. */
  isOverflowing: boolean;
  /** Must be referentially stable — a `useState` setter. The measurement
   *  re-runs whenever it changes. */
  onOverflowChange: (isOverflowing: boolean) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const isOutbound = useIsOutbound();
  const ref = useRef<HTMLDivElement>(null);

  /** Not keyed on `children`: that is a new element every render, so it tore
   *  the observer down and read `scrollHeight` back on each one — for every
   *  bubble in the feed, since they all re-render when the composer opens. The
   *  observer is what notices the content changing. */
  useLayoutEffect(() => {
    const element = ref.current;

    if (!element || isExpanded) {
      return;
    }

    const measure = () =>
      onOverflowChange(element.scrollHeight > element.clientHeight + 1);

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [isExpanded, onOverflowChange]);

  return (
    <div className="relative">
      <div
        ref={ref}
        className={cn(className, !isExpanded && 'max-h-64 overflow-hidden')}
      >
        {children}
      </div>
      {!isExpanded && isOverflowing && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-t to-transparent',
            isOutbound ? 'from-primary' : 'from-muted',
          )}
        />
      )}
    </div>
  );
}

/** The avatar's footprint, for a row that has none but still has to line up
 *  with the rows that do. */
export function MessageAvatarSpacer() {
  return <div className="size-5 shrink-0" aria-hidden />;
}

export function MessageAvatar({
  initials,
  isOutbound,
  title,
}: {
  initials: string;
  isOutbound: boolean;
  title?: string;
}) {
  return (
    <Avatar className="size-5 shrink-0" title={title}>
      <AvatarFallback
        className={cn(
          'text-[8px] font-bold',
          isOutbound
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

export function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}
